import agent from "../services/api";
import { create } from "zustand";
import { PricingPlan, CommonResponseModel, getAddOnsResponse, billingSubscriptionDetailsResponse, transactionStatusResponse} from "../models/axiosModel";
import { CURRENCY_SYMBOLS, CURRENCY_TYPES, CYCLE_TYPES } from "../components/billing/constant.billing";
import { getBillingPriceSuffix, getCurrencySymbol } from "../components/billing/utils.billing";

interface billingStoreInterface {
    isBillingSubscriptionLoading: boolean;
    isPricingPlansLoading: boolean;
    isBillingAddOnsLoading: boolean;

    pricingPlans: PricingPlan[] | null;
    setPricingPlans: (value: PricingPlan[] | null) => void;
    getPricingPlansApi: () => Promise<CommonResponseModel<PricingPlan>>;

    billingAddOns: getAddOnsResponse[] | null;
    setBillingAddOns: (value: getAddOnsResponse[] | null) => void;
    getBillingAddOnsApi: () => Promise<CommonResponseModel<getAddOnsResponse>>;

    billingSubscriptionDetails: billingSubscriptionDetailsResponse | null;
    setBillingSubscriptionDetails: (value: billingSubscriptionDetailsResponse | null) => void;
    getBillingSubscriptionDetailsApi: () => Promise<CommonResponseModel<billingSubscriptionDetailsResponse>>;

    billingCycle: string;
    billingCurrency: string;
    billingCurrencySymbol: string;
    billingPriceSuffix: string;
    setBillingTabs: (cycle: string, currency: string) => void;
    
    transactionStatuses: transactionStatusResponse[] | null;
    setTransactionStatuses: (value: transactionStatusResponse[] | null) => void;
    getTransactionStatusesApi: () => Promise<CommonResponseModel<transactionStatusResponse>>;
}

export const useBillingStore = create<billingStoreInterface>((set) => ({
    isBillingSubscriptionLoading: false,
    isPricingPlansLoading: false,
    isBillingAddOnsLoading: false,

    pricingPlans: null,
    setPricingPlans: (value: PricingPlan[] | null) => set({ pricingPlans: value }),
    getPricingPlansApi: async () => {
        set({ isPricingPlansLoading: true });

        try {
            const res = await agent.getPricingPlans.get();
            if (res?.status) {
                set({ pricingPlans: res?.data ?? null });
            } else {
                set({ pricingPlans: null });
            }
            return res;
        } finally {
            set({ isPricingPlansLoading: false });
        }
    },

    billingAddOns: null,
    setBillingAddOns: (value: getAddOnsResponse[] | null) => set({ billingAddOns: value }),
    getBillingAddOnsApi: async () => {
        set({ isBillingAddOnsLoading: true });

        try {
            const res = await agent.getAddOns.get();
            if (res?.status) {
                set({ billingAddOns: res?.data ?? null });
            } else {
                set({ billingAddOns: null });
            }
            return res;
        } finally {
            set({ isBillingAddOnsLoading: false });
        }
    },

    billingSubscriptionDetails: null,
    setBillingSubscriptionDetails: (value: billingSubscriptionDetailsResponse | null) => set({ billingSubscriptionDetails: value }),
    getBillingSubscriptionDetailsApi: async () => {
        set({ isBillingSubscriptionLoading: true });

        try {
            const res = await agent.getSubscriptionDetails.get();
            if (res?.status) {
                set({ billingSubscriptionDetails: res?.data ?? null });
            } else {
                set({ billingSubscriptionDetails: null });
            }
            return res;
        } finally {
            set({ isBillingSubscriptionLoading: false });
        }
    },

    billingCycle: CYCLE_TYPES.MONTHLY,
    billingCurrency: CURRENCY_TYPES.INR,
    billingCurrencySymbol: CURRENCY_SYMBOLS.INR,
    billingPriceSuffix: getBillingPriceSuffix(CYCLE_TYPES.MONTHLY),
    setBillingTabs: (cycle: string, currency: string) => set({
        billingCycle: cycle,
        billingCurrency: currency,
        billingCurrencySymbol: getCurrencySymbol(currency),
        billingPriceSuffix: getBillingPriceSuffix(CYCLE_TYPES.MONTHLY),
    }),

    transactionStatuses: null,
    setTransactionStatuses: (value: transactionStatusResponse[] | null) => set({ transactionStatuses: value }),
    getTransactionStatusesApi: async () => {
        const res = await agent.getTransactionStatus.get();
        if (res?.status) {
            set({ transactionStatuses: res?.data ?? null });
        } else {
            set({ transactionStatuses: null });
        }
        return res;
    },
}));
